import { Box, FormControl, Grid } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type Control, Controller } from "react-hook-form";

import { InputWrapper } from "../CredentialsDashboard/ui/InputWrapper";
import { ErrorHelperText } from "../PatientsDashboard/ui/ErrorHelper";
import {
  editableProcedureFields,
  type ProcedureFormFields,
} from "./CrudForms.zod";
import { renderField } from "./FormFieldRenderer";

interface ProcedureFormProps {
  practices: PracticeMessage[];
  supportedSources: SourceMessage[];
  control: Control<ProcedureFormFields>;
  claim: ClaimWithProcedureAndPatientMessage | null;
}

export default function ProcedureForm({
  practices,
  supportedSources,
  control,
  claim,
}: ProcedureFormProps) {
  const theme = useTheme();
  const regularFields = editableProcedureFields
    .filter((field) => field.type !== "dropdown")
    .filter((field) => field.name !== "wieldyId")
    .filter((field) => field.name !== "claimKey")
    .filter((field) => field.name !== "paymentId")
    .filter((field) => field.name !== "claimId");
  return (
    <>
      <Typography variant="h5" sx={{ fontWeight: "600" }}>
        Procedure Details
      </Typography>
      <Box sx={{ margin: `${theme.spacing(3)} 0` }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="practiceId"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="practiceId"
                      inputRef={ref}
                      label="Practice"
                      variant="outlined"
                      fullWidth
                      required
                      disabled={!!claim}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {practices.map((practice) => (
                        <MenuItem
                          key={practice.wieldyId}
                          value={practice.wieldyId}
                        >
                          {practice.displayName}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="source"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="source"
                      inputRef={ref}
                      label="Source"
                      variant="outlined"
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {supportedSources.map((source) => (
                        <MenuItem key={source.id} value={source.name}>
                          {source.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="claimId"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="claimId"
                      inputRef={ref}
                      label="Claim ID"
                      variant="outlined"
                      fullWidth
                      disabled
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="claimKey"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="claimKey"
                      inputRef={ref}
                      label="Claim Key"
                      variant="outlined"
                      fullWidth
                      disabled
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          {regularFields.map((field) =>
            renderField<ProcedureFormFields>(control, field),
          )}
        </Grid>
      </Box>
    </>
  );
}
